import { Box, Checkbox, Divider, FormControlLabel, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../../../components/UIToolkit/Popups/Dialog";

/** Employee under a role string being switched from Don't Sync to a job. `nurseId === null` = not yet
 *  linked to a nurse, so it can't be selectively held out — it syncs on the next run regardless. */
export interface SyncEmployee {
	key: string;
	nurseId: number | null;
	name: string;
	actualShiftsLast30: number;
	scheduledShiftsLast30: number;
}

interface SyncEmployeesConfirmDialogProps {
	open: boolean;
	roleString: string;
	employees: SyncEmployee[];
	/** Nurse ids to resume syncing; unlisted linked nurses are held out (ignore-from-schedule). */
	onConfirm: (syncNurseIds: number[]) => void;
	onClose: () => void;
}

/**
 * Shown when a Don't-Sync role string is mapped to a job — the inverse of DontSyncConfirmDialog.
 * Linked nurses default to selected (the intent is to sync); unchecking one holds it out. Not-yet-linked
 * employees are listed for context but can't be toggled.
 */
export const SyncEmployeesConfirmDialog: FC<SyncEmployeesConfirmDialogProps> = ({ open, roleString, employees, onConfirm, onClose }) => {
	const { t } = useTranslation();
	const linkedIds = useMemo(() => employees.filter((e) => e.nurseId !== null).map((e) => e.nurseId as number), [employees]);
	// Default all linked nurses selected; reset whenever the dialog (re)opens for a role string.
	const [selected, setSelected] = useState<Set<number>>(() => new Set());
	useEffect(() => {
		if (open) setSelected(new Set(linkedIds));
	}, [open, linkedIds]);

	const allSelected = linkedIds.length > 0 && selected.size === linkedIds.length;
	const someSelected = selected.size > 0 && !allSelected;

	const toggle = (nurseId: number) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(nurseId)) next.delete(nurseId);
			else next.add(nurseId);
			return next;
		});

	const toggleAll = () => setSelected(allSelected ? new Set() : new Set(linkedIds));

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.syncConfirm.title", { roleString })}
			dataTestId="hris-mapping-sync-confirm-dialog"
			confirm={t("hris.mapping.syncConfirm.confirm")}
			onConfirm={() => onConfirm(Array.from(selected))}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, minWidth: 380 }}>
					<Typography variant="body2" color="text.secondary">
						{t("hris.mapping.syncConfirm.description", { count: employees.length })}
					</Typography>
					{linkedIds.length > 0 && (
						<FormControlLabel
							control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} data-testid="hris-mapping-sync-select-all" />}
							label={t("hris.mapping.syncConfirm.selectAll")}
						/>
					)}
					<Divider />
					<Box sx={{ display: "flex", flexDirection: "column", maxHeight: 280, overflow: "auto" }} data-testid="hris-mapping-sync-employee-list">
						{employees.map((emp) => (
							<FormControlLabel
								key={emp.key}
								control={
									<Checkbox
										checked={emp.nurseId !== null && selected.has(emp.nurseId)}
										disabled={emp.nurseId === null}
										onChange={() => emp.nurseId !== null && toggle(emp.nurseId)}
										data-testid={`hris-mapping-sync-employee-${emp.key}`}
									/>
								}
								label={
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										<Typography variant="body2">{emp.name}</Typography>
										<Typography variant="caption" color="text.secondary">
											{emp.nurseId === null
												? t("hris.mapping.syncConfirm.willSyncNextRun")
												: t("hris.mapping.dontSyncConfirm.shiftCounts", { actual: emp.actualShiftsLast30, scheduled: emp.scheduledShiftsLast30 })}
										</Typography>
									</Box>
								}
							/>
						))}
					</Box>
				</Box>
			}
		/>
	);
};
